import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../utils/UserContext';
import { Icon, navSections, PAGE_ACCENT, hasSectionAccess, getRoleAccent, getBrandOrDefault, visibleItems, isStaff } from '../../data/map';
import AvatarRing from '../../pages/AvatarRing';
import { useValidationFlag } from '../hooks/useValidationFlag';
import ProfileMap from './ProfileMap';
import ForcedPasswordOverlay from '../../pages/ForcedPasswordOverlay';
import { useCasualAccess } from '../../utils/casualAccess';
import VideoShelf from '../VideoShelf';
import { MessageBell } from '../MessageCenter';

const SubItem = ({ item, accentColor, onClick, delay }) => (
  <div
    className="profile-sub-item"
    onClick={onClick}
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="profile-sub-item-left">
      <span className="profile-sub-icon">
        <Icon name={item.icon} color={accentColor} fallback="doc" />
      </span>
      <span className="profile-sub-label">{item.label}</span>
    </div>
    <span className="profile-sub-arrow">
      <Icon name="chevronRight" color="rgba(255,255,255,0.3)" />
    </span>
  </div>
);

const FilterGroup = ({ item, accentColor, onNavigate, isExpanded, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded]);

  return (
    <div className="profile-filter-group">
      <div className="profile-sub-item profile-filter-header" onClick={onToggle}>
        <div className="profile-sub-item-left">
          <span className="profile-sub-icon">
            <Icon name="filter" color={accentColor} />
          </span>
          <span className="profile-sub-label">{item.label}</span>
          <span className="profile-filter-count">{item.subItems.length}</span>
        </div>
        <span className={`profile-filter-chevron ${isExpanded ? 'expanded' : ''}`}>
          <Icon name="chevronDown" color={accentColor} />
        </span>
      </div>
      <div
        className="profile-filter-content"
        style={{ height: `${height}px` }}
        ref={contentRef}
      >
        {item.subItems.map((sub) => (
          <div
            key={sub.id}
            className="profile-filter-item"
            onClick={() => onNavigate(sub.route)}
          >
            <span className="profile-filter-dot" style={{ background: accentColor }} />
            <span className="profile-filter-label">{sub.label}</span>
            <span className="profile-sub-arrow">
              <Icon name="chevronRight" color="rgba(255,255,255,0.2)" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AccordionSection = ({ section, user, isOpen, onToggle, onNavigate, delay }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [openFilterId, setOpenFilterId] = useState(null);

  // Items this user can see — gated items (e.g. Work Availability, requires: 'casual')
  // drop out here, so both the list and the "N items" count agree.
  const items = visibleItems(section, user);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      const timer = setTimeout(() => {
        if (contentRef.current) setHeight(contentRef.current.scrollHeight);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, openFilterId, items.length]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      const observer = new MutationObserver(() => {
        setHeight(contentRef.current.scrollHeight);
      });
      observer.observe(contentRef.current, { subtree: true, attributes: true, childList: true });
      return () => observer.disconnect();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setOpenFilterId(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setHeight(0);
  }, [isOpen]);

  const handleFilterToggle = (filterId) => {
    setOpenFilterId(openFilterId === filterId ? null : filterId);
  };

  return (
    <div
      className={`profile-accordion ${isOpen ? 'open' : ''}`}
      style={{ animationDelay: `${delay}ms`, '--accent': section.accentColor }}
    >
      <div className="profile-accordion-header" onClick={onToggle}>
        <div className="profile-accordion-left">
          <div
            className="profile-accordion-icon"
            style={{
              background: `${section.accentColor}15`,
              borderColor: `${section.accentColor}30`,
            }}
          >
            {/* image (e.g. IGLU logo) wins over the glyph when a section defines one */}
            <Icon name={section.icon} image={section.image} alt={section.label} color={section.accentColor} fallback="doc" />
          </div>
          <div className="profile-accordion-info">
            <span className="profile-accordion-label">{section.label}</span>
            <span className="profile-accordion-count">{items.length} items</span>
          </div>
        </div>
        <div className={`profile-accordion-chevron ${isOpen ? 'rotated' : ''}`}>
          <Icon
            name="chevronDown"
            color={isOpen ? section.accentColor : 'rgba(255,255,255,0.4)'}
          />
        </div>
      </div>

      <div className="profile-accordion-content" style={{ height: `${height}px` }}>
        <div ref={contentRef} className="profile-accordion-inner">
          {items.map((item, idx) =>
            item.isExpandable ? (
              <FilterGroup
                key={item.id}
                item={item}
                accentColor={section.accentColor}
                onNavigate={onNavigate}
                isExpanded={openFilterId === item.id}
                onToggle={() => handleFilterToggle(item.id)}
              />
            ) : (
              <SubItem
                key={item.id}
                item={item}
                accentColor={section.accentColor}
                onClick={() => onNavigate(item.route)}
                delay={idx * 40}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   ProfileHome
   ────────────────────────────────────────────── */

const ProfileHome = () => {
  const { user, loading, setUser, setHomeAccent } = useUser();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);
  const { clear } = useValidationFlag();

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user]);

  // Casual check — hooks must run on every render, so this stays ABOVE the
  // `if (!user) return null` early return. Only staff are checked, and only
  // until the flag is known; after that isCasual lives on the user object
  // for the accordion, ProfileMap and FooterNav to read.
  const { access } = useCasualAccess(!!user && isStaff(user) && user.isCasual === undefined);
  useEffect(() => {
    if (access && user && user.isCasual !== access.casual) {
      setUser({ ...user, isCasual: access.casual });
    }
  }, [access]);

  if (!user) return null;

  const userName = user.userName || '';
  const userJob = user.userJob || '';
  const email = user.email || '';

  // Sections by userJob (staff) OR by Group (IGLU / Client) — see map.js
  const visibleSections = navSections.filter(
    (section) => hasSectionAccess(section.id, user)
  );

  // Pass the whole user so Group = 'Client' → blue, IGLU → orange, staff → their role colour
  const roleAccent = getRoleAccent(user);
  const brand = getBrandOrDefault(user);

  const handleToggle = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const handleNavigate = (route, sectionId) => {
    setHomeAccent(PAGE_ACCENT[sectionId] || roleAccent);
    navigate(route);
  };

  const handleLogout = () => {
    clear();
    navigate('/validlogin');
    window.location.reload();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="profile-home-container">
      <div className="profile-bg-layer" />
      <div className="profile-bg-grain" />

      <div className="profile-home-content">
        <div className="profile-home-header">
          <div className="profile-greeting-block">
            <span className="profile-greeting">{getGreeting()}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <h1 className="profile-name">{userName || email}</h1>
              <AvatarRing size={64} ringWidth={3} showName={false} showRole={false} homeRoute={null} accent={roleAccent} />
              {/* Messages bell (app-wide): mounting it here also turns on polling and arrival toasts for this
                  page. Keep the word "global" out of the first position: ESLint treats a block comment that
                  starts with it as a globals pragma and parses the rest of the sentence as variable names. */}
              <MessageBell accent={roleAccent} label="Global Messages" className="mc-bell-home" />
            </div>
            <span className="profile-role-badge" style={{ borderColor: roleAccent }}>
              {userJob}
            </span>
          </div>
        </div>

        {/* training / help videos for this user's sections — data/videoLibrary.js */}
        <VideoShelf />

        <div className="profile-nav-list">
          {visibleSections.map((section, idx) => (
            <div
              key={section.id}
              className={
                section.responsive === 'mobile-only'
                  ? 'responsive-mobile-only'
                  : section.responsive === 'desktop-only'
                    ? 'responsive-desktop-only'
                    : ''
              }
            >
              <AccordionSection
                section={section}
                user={user}
                isOpen={openSection === section.id}
                onToggle={() => handleToggle(section.id)}
                onNavigate={(route) => handleNavigate(route, section.id)}
                delay={idx * 80}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 24 }}>
          <ProfileMap visibleSections={visibleSections} user={user} userJob={userJob} accent={roleAccent} />
        </div>

        <div className="profile-logout-section">
          <button className="profile-logout-btn" onClick={handleLogout}>
            <span className="profile-logout-icon">
              <Icon name="logout" color="#ef4444" />
            </span>
            <span className="profile-logout-label">Log Out</span>
          </button>
        </div>
      </div>
      {user.password_needs_change && <ForcedPasswordOverlay />}
    </div>
  );
};

export default ProfileHome;